import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Camera, Upload, Trash2, RefreshCw, Plus,
  Check, X, AlertTriangle, FileText, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { Archive } from 'lucide-react';
import TenantMobileLayout from './TenantMobileLayout';
import { ArchivioDDT } from '../ddt/ArchivioDDT';

const TAB_CLS = 'flex-1 py-2.5 rounded-xl text-xs font-medium transition-all flex flex-col items-center justify-center gap-1';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Formattazione valuta italiana
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
  const headers = { 'Authorization': `Bearer ${token}` };
  let tenantDb = localStorage.getItem('tenant_db');
  if (!tenantDb) {
    try {
      const tenantList = JSON.parse(localStorage.getItem('tenant_list') || '[]');
      if (tenantList.length > 0 && tenantList[0].database_name) {
        tenantDb = tenantList[0].database_name;
      }
    } catch (e) {}
  }
  if (!tenantDb) {
    try {
      const tenantUser = JSON.parse(localStorage.getItem('tenant_user') || '{}');
      if (tenantUser.tenants && tenantUser.tenants[0]) {
        tenantDb = tenantUser.tenants[0].database_name;
      }
    } catch (e) {}
  }
  if (tenantDb) {
    headers['X-Tenant-DB'] = tenantDb.replace(/^tenant_/, '');
  }
  return headers;
};

const TenantMobileDDT = ({ onBack, setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState('attesa'); // attesa, ricevuti, tutti
  const [loading, setLoading] = useState(true);
  const [ddtList, setDdtList] = useState([]);
  const [expandedDdt, setExpandedDdt] = useState({});
  
  // Upload DDT esistente
  const [selectedDDT, setSelectedDDT] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analisiResult, setAnalisiResult] = useState(null);

  // Nuova Bolla (senza ordine)
  const [showNuovaBollaModal, setShowNuovaBollaModal] = useState(false);
  const [nuovaBollaAnalisi, setNuovaBollaAnalisi] = useState(null);
  const [analyzingNuova, setAnalyzingNuova] = useState(false);

  useEffect(() => {
    if (activeTab !== 'archivio') loadDDT();
  }, [activeTab]);

  const loadDDT = async () => {
    setLoading(true);
    try {
      let url = `${API}/gestione/ddt`;
      if (activeTab === 'attesa') {
        url += '?solo_attesa=true';
      } else if (activeTab === 'ricevuti') {
        url += '?status=ricevuto';
      }
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      let ddt = res.data.ddt || [];
      
      // Filtra in base al tab
      if (activeTab === 'attesa') {
        ddt = ddt.filter(d => d.status === 'in_attesa');
      } else if (activeTab === 'ricevuti') {
        ddt = ddt.filter(d => d.status === 'ricevuto');
      }
      
      setDdtList(ddt);
    } catch (error) {
      console.error('Errore caricamento DDT:', error);
      
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedDdt(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const eliminaDDT = async (id) => {
    if (!window.confirm('Eliminare questo DDT?')) return;
    try {
      await axios.delete(`${API}/gestione/ddt/${id}`, { headers: getAuthHeaders() });
      toast.success('DDT eliminato');
      loadDDT();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Upload DDT per un ordine esistente
  const handleUploadDDT = async (file) => {
    if (!selectedDDT) return;
    
    setAnalyzing(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          const isPdf = file.type.includes('pdf');
          
          const endpoint = isPdf 
            ? `${API}/gestione/ddt/analizza-documento`
            : `${API}/gestione/ddt/analizza-immagine`;
          
          const payload = isPdf
            ? { file_base64: base64Data, tipo_file: 'pdf' }
            : { immagine_base64: base64Data };
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            const analisi = res.data.analisi || res.data.dati;
            setAnalisiResult(analisi);
            toast.success(`${analisi.prodotti?.length || 0} prodotti trovati`);
          } else {
            toast.error(res.data.detail || 'Errore analisi');
          }
        } catch (error) {
          toast.error('Errore analisi documento');
        } finally {
          setAnalyzing(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore lettura file');
      setAnalyzing(false);
    }
  };

  // Upload NUOVA bolla (senza ordine)
  const handleUploadNuovaBolla = async (file) => {
    setAnalyzingNuova(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result;
          const isPdf = file.type.includes('pdf');
          
          const endpoint = isPdf 
            ? `${API}/gestione/ddt/analizza-documento`
            : `${API}/gestione/ddt/analizza-immagine`;
          
          const payload = isPdf
            ? { file_base64: base64Data, tipo_file: 'pdf' }
            : { immagine_base64: base64Data };
          
          const res = await axios.post(endpoint, payload, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            timeout: 180000
          });
          
          if (res.data.success) {
            const analisi = res.data.analisi || res.data.dati;
            setNuovaBollaAnalisi(analisi);
            toast.success(`${analisi.prodotti?.length || 0} prodotti trovati`);
          } else {
            toast.error(res.data.detail || 'Errore analisi');
          }
        } catch (error) {
          console.error('Errore analisi:', error);
          toast.error('Errore analisi documento');
        } finally {
          setAnalyzingNuova(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Errore lettura file');
      setAnalyzingNuova(false);
    }
  };

  const salvaDDTRicevuto = async () => {
    if (!selectedDDT || !analisiResult || analyzing) return;
    
    setAnalyzing(true);
    try {
      await axios.put(`${API}/gestione/ddt/${selectedDDT.id}`, {
        status: 'ricevuto',
        data_ricezione: new Date().toISOString(),
        prodotti: analisiResult.prodotti || [],
        fornitore: analisiResult.fornitore || selectedDDT.fornitore,
        numero_ddt: analisiResult.numero_ddt,
        data_ddt: analisiResult.data_ddt
      }, { headers: getAuthHeaders() });
      
      toast.success('DDT salvato!');
      setShowUploadModal(false);
      setSelectedDDT(null);
      setAnalisiResult(null);
      loadDDT();
    } catch (error) {
      toast.error('Errore salvataggio');
    } finally {
      setAnalyzing(false);
    }
  };

  // Salva NUOVA bolla (senza ordine)
  const salvaNuovaBolla = async () => {
    if (!nuovaBollaAnalisi || analyzingNuova) return;
    
    setAnalyzingNuova(true);
    try {
      // Crea nuovo DDT
      await axios.post(`${API}/gestione/ddt`, {
        status: 'ricevuto',
        fornitore: nuovaBollaAnalisi.fornitore || 'Da definire',
        numero: nuovaBollaAnalisi.numero_ddt || `AUTO-${Date.now()}`,
        data_ddt: nuovaBollaAnalisi.data_ddt || new Date().toISOString().split('T')[0],
        data_ricezione: new Date().toISOString(),
        prodotti: nuovaBollaAnalisi.prodotti || [],
        totale: nuovaBollaAnalisi.totale || 0,
        note: 'Caricato manualmente senza ordine'
      }, { headers: getAuthHeaders() });
      
      toast.success('Bolla salvata!');
      chiudiNuovaBollaModal();
      loadDDT();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore salvataggio');
    } finally {
      setAnalyzingNuova(false);
    }
  };

  const chiudiNuovaBollaModal = () => {
    setShowNuovaBollaModal(false);
    setNuovaBollaAnalisi(null);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'in_attesa':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'ricevuto':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_attesa': return 'In Attesa';
      case 'ricevuto': return 'Ricevuto';
      default: return status;
    }
  };

  return (
    <TenantMobileLayout title="DDT / Bolle" onBack={onBack}>
      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('attesa')}
          className={`${TAB_CLS} ${
            activeTab === 'attesa'
              ? 'bg-orange-600 text-white'
              : 'bg-black/40 text-gray-400 border border-orange-500/30'
          }`}
          data-testid="tab-attesa"
        >
          <AlertTriangle className="h-4 w-4" />
          In Attesa
        </button>
        <button
          onClick={() => setActiveTab('ricevuti')}
          className={`${TAB_CLS} ${
            activeTab === 'ricevuti'
              ? 'bg-green-600 text-white'
              : 'bg-black/40 text-gray-400 border border-green-500/30'
          }`}
          data-testid="tab-ricevuti"
        >
          <Check className="h-4 w-4" />
          Ricevuti
        </button>
        <button
          onClick={() => setActiveTab('tutti')}
          className={`${TAB_CLS} ${
            activeTab === 'tutti'
              ? 'bg-cyan-500 text-black'
              : 'bg-black/40 text-gray-400 border border-cyan-500/30'
          }`}
          data-testid="tab-tutti"
        >
          <FileText className="h-4 w-4" />
          Tutti
        </button>
        <button
          onClick={() => setActiveTab('archivio')}
          className={`${TAB_CLS} ${
            activeTab === 'archivio'
              ? 'bg-purple-600 text-white'
              : 'bg-black/40 text-gray-400 border border-purple-500/30'
          }`}
          data-testid="tab-archivio"
        >
          <Archive className="h-4 w-4" />
          Archivio
        </button>
      </div>

      {activeTab === 'archivio' ? <ArchivioDDT /> : (<>

      {/* Tasto Nuova Bolla - naviga a Gestione Bolla */}
      <button
        onClick={() => setCurrentPage && setCurrentPage('bolle', { openNuovaBolla: true })}
        className="w-full mb-4 py-4 rounded-2xl flex items-center justify-center gap-2 text-white font-semibold active:scale-98 transition-all"
        style={{ background: 'linear-gradient(135deg, rgba(147,51,234,1) 0%, rgba(79,70,229,1) 100%)' }}
        data-testid="btn-nuova-bolla"
      >
        <Plus className="h-5 w-5" />
        Nuova Bolla
      </button>

      {/* Lista DDT */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      ) : ddtList.length > 0 ? (
        <div className="space-y-3">
          {ddtList.map((ddt) => (
            <div 
              key={ddt.id}
              className={`rounded-2xl overflow-hidden border ${getStatusStyle(ddt.status).split(' ').filter(c => c.startsWith('border')).join(' ')}`}
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
            >
              {/* Header - cliccabile per espandere */}
              <div 
                className="p-4 active:bg-white/5"
                onClick={() => toggleExpand(ddt.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white">DDT #{ddt.numero || 'N/D'}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(ddt.status)}`}>
                        {getStatusLabel(ddt.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">{ddt.fornitore}</p>
                    <p className="text-xs text-gray-500">
                      {ddt.data_ddt || ddt.data_creazione || 'Data N/D'} 
                      {ddt.prodotti_attesi?.length > 0 && ` • ${ddt.prodotti_attesi.length} prodotti`}
                      {ddt.prodotti?.length > 0 && ` • ${ddt.prodotti.length} prodotti`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {expandedDdt[ddt.id] ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Dettagli espansi */}
              {expandedDdt[ddt.id] && (
                <div className="border-t border-white/10 p-4 bg-black/30">
                  {/* Prodotti */}
                  {(ddt.prodotti_attesi || ddt.prodotti || []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-400 mb-2">
                        {ddt.status === 'in_attesa' ? 'Prodotti ordinati:' : 'Prodotti ricevuti:'}
                      </p>
                      <div className="space-y-1">
                        {(ddt.prodotti_attesi || ddt.prodotti || []).map((p, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-300">{p.nome}</span>
                            <span className="text-white font-medium">
                              {p.quantita} {p.unita_misura}
                              {p.prezzo_unitario && ` • ${formatCurrency(p.prezzo_unitario)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Azioni */}
                  <div className="flex gap-2">
                    {ddt.status === 'in_attesa' && (
                      <button
                        onClick={() => {
                          setSelectedDDT(ddt);
                          setShowUploadModal(true);
                        }}
                        className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                      >
                        <Camera className="h-4 w-4" />
                        Carica DDT
                      </button>
                    )}
                    {ddt.status === 'ricevuto' && (
                      <button
                        className="flex-1 py-3 bg-cyan-500/20 text-cyan-400 rounded-xl font-medium flex items-center justify-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Visualizza
                      </button>
                    )}
                    <button
                      onClick={() => eliminaDDT(ddt.id)}
                      className="p-3 bg-red-500/20 text-red-400 rounded-xl"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-center py-12 rounded-2xl border border-cyan-500/20"
          style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
        >
          <Package className="h-12 w-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">
            {activeTab === 'attesa' && 'Nessun DDT in attesa'}
            {activeTab === 'ricevuti' && 'Nessun DDT ricevuto'}
            {activeTab === 'tutti' && 'Nessun DDT'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Usa "Carica DDT" per aggiungere una bolla
          </p>
        </div>
      )}
      </>)}

      {/* ============================================ */}
      {/* Modal: Carica DDT (NUOVA BOLLA senza ordine) */}
      {/* ============================================ */}
      {showNuovaBollaModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-purple-500/20 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {nuovaBollaAnalisi ? 'Verifica Bolla' : 'Carica DDT'}
                </h2>
                <p className="text-sm text-gray-400">Senza ordine associato</p>
              </div>
              <button onClick={chiudiNuovaBollaModal} className="p-2 bg-black/40 rounded-xl">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {!nuovaBollaAnalisi ? (
                <>
                  {/* Info */}
                  <div className="bg-purple-500/10 rounded-xl p-3 border border-purple-500/20">
                    <p className="text-sm text-purple-400">
                      Carica una bolla/DDT ricevuta senza ordine associato. 
                      Verrà analizzata automaticamente.
                    </p>
                  </div>
                  
                  {/* Upload buttons */}
                  <div className="space-y-3">
                    <label className="block">
                      <div className="w-full py-8 bg-black/40 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center gap-2 active:bg-white/5">
                        <Camera className="h-10 w-10 text-purple-400" />
                        <span className="text-white font-medium">Scatta Foto</span>
                        <span className="text-xs text-gray-500">o tocca per caricare</span>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment"
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadNuovaBolla(e.target.files[0])}
                      />
                    </label>
                    
                    <label className="block">
                      <div className="w-full py-4 bg-black/40 border border-purple-500/30 rounded-xl flex items-center justify-center gap-2 active:bg-white/5">
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-300">Carica PDF</span>
                      </div>
                      <input 
                        type="file" 
                        accept=".pdf" 
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadNuovaBolla(e.target.files[0])}
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Risultati analisi */}
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                    <p className="text-green-400 font-medium mb-2">
                      Analisi completata: {nuovaBollaAnalisi.prodotti?.length || 0} prodotti
                    </p>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Fornitore</p>
                        <p className="text-white font-medium">{nuovaBollaAnalisi.fornitore || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Data</p>
                        <p className="text-white font-medium">{nuovaBollaAnalisi.data_ddt || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">N° DDT</p>
                        <p className="text-white font-medium">{nuovaBollaAnalisi.numero_ddt || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Totale</p>
                        <p className="text-cyan-400 font-bold">{formatCurrency(nuovaBollaAnalisi.totale)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista prodotti estratti */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-400">Prodotti trovati:</p>
                    {nuovaBollaAnalisi.prodotti?.map((p, idx) => (
                      <div key={idx} className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                        <div className="flex justify-between">
                          <span className="text-white flex-1">{p.nome}</span>
                          <span className="text-cyan-400 font-medium ml-2">
                            {p.quantita} {p.unita_misura}
                          </span>
                        </div>
                        {p.prezzo_unitario && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatCurrency(p.prezzo_unitario)}/u • Tot: {formatCurrency(p.importo || p.quantita * p.prezzo_unitario)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {nuovaBollaAnalisi && (
              <div className="p-4 border-t border-purple-500/20 flex-shrink-0">
                <button
                  onClick={salvaNuovaBolla}
                  disabled={analyzingNuova}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  {analyzingNuova ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Check className="h-5 w-5" />
                  )}
                  Salva Bolla
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Upload DDT esistente */}
      {showUploadModal && selectedDDT && (
        <div className="fixed inset-0 bg-black/80 flex items-end z-50">
          <div 
            className="w-full rounded-t-3xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white">Carica DDT</h2>
                <p className="text-sm text-gray-400">{selectedDDT.fornitore}</p>
              </div>
              <button 
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedDDT(null);
                  setAnalisiResult(null);
                }} 
                className="p-2 bg-black/40 rounded-xl"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {!analisiResult ? (
                <>
                  {/* Prodotti attesi */}
                  {selectedDDT.prodotti_attesi?.length > 0 && (
                    <div className="bg-orange-500/10 rounded-xl p-3 border border-orange-500/20">
                      <p className="text-xs font-medium text-orange-400 mb-2">Prodotti ordinati:</p>
                      {selectedDDT.prodotti_attesi.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-300">{p.nome}</span>
                          <span className="text-white">{p.quantita} {p.unita_misura}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload buttons */}
                  <div className="space-y-3">
                    <label className="block">
                      <div className="w-full py-6 bg-black/40 border-2 border-dashed border-cyan-500/30 rounded-xl flex flex-col items-center justify-center gap-2 active:bg-white/5">
                        <Camera className="h-8 w-8 text-cyan-400" />
                        <span className="text-white font-medium">Scatta Foto</span>
                        <span className="text-xs text-gray-500">o tocca per caricare</span>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment"
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadDDT(e.target.files[0])}
                      />
                    </label>
                    
                    <label className="block">
                      <div className="w-full py-4 bg-black/40 border border-cyan-500/30 rounded-xl flex items-center justify-center gap-2 active:bg-white/5">
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-300">Carica PDF</span>
                      </div>
                      <input 
                        type="file" 
                        accept=".pdf" 
                        className="hidden" 
                        onChange={(e) => e.target.files?.[0] && handleUploadDDT(e.target.files[0])}
                      />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  {/* Risultati analisi */}
                  <div className="bg-green-500/10 rounded-xl p-3 border border-green-500/20">
                    <p className="text-green-400 font-medium mb-2">
                      Analisi completata: {analisiResult.prodotti?.length || 0} prodotti
                    </p>
                    {analisiResult.fornitore && (
                      <p className="text-sm text-gray-400">Fornitore: {analisiResult.fornitore}</p>
                    )}
                    {analisiResult.numero_ddt && (
                      <p className="text-sm text-gray-400">DDT: {analisiResult.numero_ddt}</p>
                    )}
                  </div>
                  
                  {/* Lista prodotti estratti */}
                  <div className="space-y-2">
                    {analisiResult.prodotti?.map((p, idx) => (
                      <div key={idx} className="bg-black/40 rounded-xl p-3 border border-cyan-500/20">
                        <div className="flex justify-between">
                          <span className="text-white">{p.nome}</span>
                          <span className="text-cyan-400 font-medium">
                            {p.quantita} {p.unita_misura}
                          </span>
                        </div>
                        {p.prezzo_unitario && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatCurrency(p.prezzo_unitario)}/u • Tot: {formatCurrency(p.importo || p.quantita * p.prezzo_unitario)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {analisiResult && (
              <div className="p-4 border-t border-cyan-500/20 flex-shrink-0">
                <button
                  onClick={salvaDDTRicevuto}
                  disabled={analyzing}
                  className="w-full py-4 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  {analyzing ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Check className="h-5 w-5" />
                  )}
                  Conferma Ricezione
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {(analyzing || analyzingNuova) && !analisiResult && !nuovaBollaAnalisi && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <RefreshCw className="h-12 w-12 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-white">Analisi in corso...</p>
            <p className="text-sm text-gray-400 mt-1">Potrebbe richiedere alcuni secondi</p>
          </div>
        </div>
      )}
    </TenantMobileLayout>
  );
};

export default TenantMobileDDT;
